
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CountPill } from '@/components/ui/count-pill';
import { formatCount } from '@/lib/format-count';
import React from 'react';

type StarRatingProps = {
  rating: number;
  totalReviews?: number;
  className?: string;
  /**
   * One star, the score and the count ("★ 4.8 (12)") instead of five stars and
   * a pill. For product cards, where the rating shares a row with the seller's
   * name: five stars and a pill take about 112px, which left a phone's
   * two-column card with no room for the name.
   */
  compact?: boolean;
};

const StarRatingComponent = ({ rating, totalReviews, className, compact = false }: StarRatingProps) => {
  if (compact) {
    return (
      <div className={cn('flex items-center gap-1 text-xs', className)}>
        <Star className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--star))] fill-[hsl(var(--star))]" aria-hidden />
        <span className="font-medium tabular-nums">
          {rating.toFixed(1)}
          <span className="sr-only"> out of 5 stars</span>
        </span>
        {/* Below sm (a phone's two-column card, about 160px wide) the count
            is left to screen readers, so the seller's name keeps its room
            beside the score. */}
        {totalReviews !== undefined && (
          <span className="tabular-nums text-muted-foreground max-sm:sr-only">
            ({formatCount(totalReviews)}<span className="sr-only">{totalReviews === 1 ? ' review' : ' reviews'}</span>)
          </span>
        )}
      </div>
    );
  }

  const fullStars = Math.floor(rating);
  const partialStarFill = Math.round((rating % 1) * 100);

  const filledClass = "h-4 w-4 text-[hsl(var(--star))] fill-[hsl(var(--star))]";
  const emptyClass = "h-4 w-4 text-[hsl(var(--star)/0.3)]";

  return (
    <div className={cn('flex items-center gap-2 text-sm', className)}>
      {/* The stars are pictures of the score; this is the score. */}
      <span className="sr-only">Rated {rating.toFixed(1)} out of 5 stars</span>
      <div className="flex items-center" aria-hidden="true">
        {[...Array(5)].map((_, i) => {
            const starNumber = i + 1;
            if (starNumber <= fullStars) {
                return <Star key={`full-${i}`} className={filledClass} />;
            }
            if (starNumber === fullStars + 1 && partialStarFill > 0) {
                return (
                    <div key={`partial-${i}`} className="relative h-4 w-4">
                        <Star className={emptyClass} />
                        <div style={{ width: `${partialStarFill}%` }} className="absolute inset-0 overflow-hidden">
                            <Star className={filledClass} />
                        </div>
                    </div>
                );
            }
            return <Star key={`empty-${i}`} className={emptyClass} />;
        })}
      </div>
      {totalReviews !== undefined && <CountPill count={totalReviews} className="ml-0" />}
    </div>
  );
}

export const StarRating = React.memo(StarRatingComponent);
